import React, { createContext, FC, memo, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { FormikErrors, FormikTouched, useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import mapValues from 'lodash/mapValues';
import moment from 'moment';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import {
  IESSAvailabilityCycleDTO,
  IUpsertAvailabilityCycleRequestDTO,
  IUpsertAvailabilityPreferencesRequestDTO,
} from 'src/api/dtos';
import AvailabilityRepository from 'src/api/repositories/ess/v1/availability.repository';
import { IError } from 'src/api/types';
import {
  AvailabilityCycleInputsType,
  EditedCyclePeriodDateType,
  PreferenceInputsType,
} from 'src/features/availabilities/availabilities.types';
import { buildEditCycleInputs, getAvailabilityDateText } from 'src/features/availabilities/availabilities.utils';
import { Modal } from 'src/shared/components';
import { DATE_TIME_FORMAT } from 'src/shared/constants';
import { AvailabilityStatusEnums, CycleTypeEnums, OperationTypeEnums } from 'src/shared/enums';
import { usePrevious } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { getErrorMessage, getSecondsFromTime } from 'src/shared/utils';

const uninitializedCycle: IESSAvailabilityCycleDTO = {
  id: -1,
  employeeId: -1,
  cycleStart: '',
  validFrom: '',
  cycleLength: -1,
  type: CycleTypeEnums.WEEKLY,
  preferences: [],
  isLocked: false,
};

type EmployeeAvailabilityFutureCyclesProps = {
  inputsList: AvailabilityCycleInputsType[] | undefined;
  loading: boolean;
  touched: FormikTouched<AvailabilityCycleInputsType[]>;
  errors: FormikErrors<AvailabilityCycleInputsType[]>;
  apiError: string;
  // Cycle
  updatingCycle: boolean;
  openCycle: number | undefined;
  editedCycle: number | undefined;
  onOpenCycleToggle: (cycleIndex: number) => void;
  onEditedCycleChange: (cycleIndex: number) => void;
  onStartDateChange: (date: moment.Moment | null, cycleIndex: number) => void;
  onCancelCycle: () => void;
  onSaveCycle: () => void;
  // Preferences
  updatingPreferences: boolean;
  editedCyclePeriodDate: EditedCyclePeriodDateType | undefined;
  onEditedCyclePeriodDateChange: (cycleIndex: number, periodKey: string, cycleKey: string) => void;
  onChangeFullDay: (wasAllDay: boolean, cycleIndex: number, periodKey: string, dateKey: string) => void;
  onChangeStatus: (
    status: AvailabilityStatusEnums,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => void;
  onDelete: (cycleIndex: number, periodKey: string, dateKey: string, preferenceIndex: number) => void;
  onAdd: (cycleIndex: number, periodKey: string, dateKey: string) => void;
  onChangeStartTime: (
    value: string | undefined,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => void;
  onChangeEndTime: (
    value: string | undefined,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => void;
  onCancelPreferences: () => void;
  onSavePreferences: () => void;
  onDeleteCycle: (cycleIndex: number) => void;
  onDeleteDate: (cycleIndex: number, periodKey: string, dateKey: string) => void;
};

const EmployeeAvailabilityFutureCyclesContext = createContext<EmployeeAvailabilityFutureCyclesProps>(
  {} as EmployeeAvailabilityFutureCyclesProps,
);

export const useEmployeeAvailabilityFutureCycles = (): EmployeeAvailabilityFutureCyclesProps =>
  useContext<EmployeeAvailabilityFutureCyclesProps>(EmployeeAvailabilityFutureCyclesContext);

interface Props {
  futureCycles: IESSAvailabilityCycleDTO[] | undefined;
  loading: boolean;
  children: ReactNode;
}

export const EmployeeAvailabilityFutureCyclesProvider: FC<Props> = memo(({ children, futureCycles, loading }) => {
  const { t } = useTranslate();

  const [openCycle, setOpenCycle] = useState<number | undefined>(0);
  const [editedCycle, setEditedCycle] = useState<number | undefined>();
  const [editedCyclePeriodDate, setEditedCyclePeriodDate] = useState<EditedCyclePeriodDateType>();
  const [apiError, setApiError] = useState('');

  const [updateCycle, { isLoading: isUpdatingCycle }] = AvailabilityRepository.endpoints.useUpdateCycleMutation();

  const [updatePreferences, { isLoading: isUpdatingPreferences }] =
    AvailabilityRepository.endpoints.useUpdatePreferencesMutation();

  Yup.setLocale({
    mixed: {
      required: t('global.requiredField'),
    },
  });

  const handleUpsertCycle = async () => {
    if (isNil(editedCycle)) {
      return;
    }

    const inputs = inputsList[editedCycle];

    if (!inputs) {
      return;
    }

    const payload: IUpsertAvailabilityCycleRequestDTO = {
      id: inputs.id,
      dayOfWeek: moment(inputs.cycleStart).day(),
      type: inputs.type,
      operationType: OperationTypeEnums.UPDATE,
      cycleStart: inputs.cycleStart,
      validFrom: inputs.validFrom,
      cycleLength: inputs.cycleLength,
    };

    try {
      await updateCycle(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyUpdatedYourCycle'), { type: toast.TYPE.SUCCESS });
      setEditedCycle(undefined);
      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    }
  };

  const handleUpsertPreferences = async () => {
    if (!editedCyclePeriodDate) {
      return;
    }

    const { cycleIndex, periodKey, dateKey } = editedCyclePeriodDate;

    const inputs = inputsList[cycleIndex];
    const preferences = inputsList[cycleIndex]?.groups[periodKey]?.[dateKey];

    if (!inputs || !preferences.length) {
      return;
    }

    const [sharedPreference] = preferences;

    const dayOfCycle = sharedPreference.dayOfCycle;

    const payload: IUpsertAvailabilityPreferencesRequestDTO = {
      cycleId: inputs.id,
      dayOfCycle,
      preferences: preferences.map(({ operationType, status, id, startTimeInSeconds, endTimeInSeconds }) => ({
        operationType,
        status,
        id,
        startTimeInSeconds,
        endTimeInSeconds,
      })),
    };

    try {
      await updatePreferences(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyUpdatedYourPreferences'), { type: toast.TYPE.SUCCESS });
      setEditedCyclePeriodDate(undefined);
      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    }
  };

  const cycleValidationSchema = Yup.array().of(
    Yup.object().shape({
      // Yup lazy is used to validate groups as it's an object with dynamic keys
      groups: Yup.lazy((periods) =>
        Yup.object(
          mapValues(periods, () =>
            Yup.lazy((dates) =>
              Yup.object(
                mapValues(dates, () =>
                  Yup.array().of(
                    Yup.object({
                      status: Yup.mixed<AvailabilityStatusEnums>()
                        .test((value) =>
                          Object.values(AvailabilityStatusEnums).includes(value as AvailabilityStatusEnums),
                        )
                        .required(),
                      operationType: Yup.mixed<OperationTypeEnums>()
                        .test((value) => Object.values(OperationTypeEnums).includes(value as OperationTypeEnums))
                        .required(),
                      id: Yup.number().optional(),
                      startTimeInSeconds: Yup.number().optional(),
                      endTimeInSeconds: Yup.number().optional(),
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    }),
  );

  const {
    values: inputsList,
    setValues,
    setFieldValue,
    handleSubmit: handleSavePreferences,
    initialValues,
    touched,
    errors,
    resetForm,
  } = useFormik<AvailabilityCycleInputsType[]>({
    enableReinitialize: true,
    initialValues: futureCycles
      ? futureCycles.map((futureCycle) => buildEditCycleInputs(futureCycle))
      : [buildEditCycleInputs(uninitializedCycle)],
    validationSchema: cycleValidationSchema,
    onSubmit: handleUpsertPreferences,
  });

  const isInitial = isEqual(inputsList, [buildEditCycleInputs(uninitializedCycle)]);

  const previousCurrentCycle = usePrevious(futureCycles);

  useEffect(() => {
    if (!futureCycles || isEqual(previousCurrentCycle, futureCycles)) {
      return;
    }

    const currentOpenCycle = futureCycles.find(({ id }) => id === openCycle);

    setValues(futureCycles.map((futureCycle) => buildEditCycleInputs(futureCycle)));
    setOpenCycle(currentOpenCycle?.id ?? futureCycles[0]?.id);
    resetForm();
  }, [futureCycles]);

  /**
   * Cycle
   */

  const handleOpenCycleToggle = (cycleId: number) => {
    if (openCycle === cycleId) {
      setOpenCycle(undefined);
    } else {
      setOpenCycle(cycleId);
    }
  };

  const handleEditedCycleChange = (cycleIndex: number) => {
    resetForm();
    setApiError('');
    setEditedCycle(cycleIndex);
  };

  const handleStartDateChange = (date: moment.Moment | null, cycleIndex: number) => {
    if (!date) {
      return;
    }

    setFieldValue(`[${cycleIndex}].validFrom`, date.format(DATE_TIME_FORMAT));
  };

  const handleCancelCycle = () => {
    setEditedCycle(undefined);
    setApiError('');
    resetForm();
  };

  /**
   * Preferences
   */

  const handleEditedCyclePeriodDateChange = (cycleIndex: number, periodKey: string, dateKey: string) => {
    resetForm();
    setApiError('');
    setEditedCyclePeriodDate({ cycleIndex, periodKey, dateKey });
  };

  const handleChangeFullDay = (wasAllDay: boolean, cycleIndex: number, periodKey: string, dateKey: string) => {
    const initialPreferences = initialValues[cycleIndex].groups[periodKey][dateKey];

    const hasInitialAllDay = !initialPreferences.some(
      ({ startTimeInSeconds, endTimeInSeconds }) => !isNil(startTimeInSeconds) || !isNil(endTimeInSeconds),
    );

    if (wasAllDay) {
      setFieldValue(
        `[${cycleIndex}].groups[${periodKey}][${dateKey}]`,
        initialPreferences.map((preference) => ({
          ...preference,
          startTimeInSeconds: preference.startTimeInSeconds ?? 0,
          endTimeInSeconds: preference.endTimeInSeconds ?? 0,
        })),
      );
    } else {
      setFieldValue(
        `[${cycleIndex}].groups[${periodKey}][${dateKey}]`,
        hasInitialAllDay
          ? [...initialPreferences]
          : initialPreferences.map((preference, index) => ({
              ...preference,
              startTimeInSeconds: undefined,
              endTimeInSeconds: undefined,
              operationType: index === 0 ? OperationTypeEnums.UPDATE : OperationTypeEnums.DELETE,
            })),
      );
    }
  };

  const handleChangeStatus = (
    status: AvailabilityStatusEnums,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(
      `[${cycleIndex}].groups[${periodKey}][${dateKey}][${preferenceIndex}].status`,
      status === AvailabilityStatusEnums.AVAILABLE
        ? AvailabilityStatusEnums.NOT_AVAILABLE
        : AvailabilityStatusEnums.AVAILABLE,
    );
  };

  const handleDeleteOverride = (cycleIndex: number, periodKey: string, dateKey: string, preferenceIndex: number) => {
    const preferences = inputsList[cycleIndex].groups[periodKey][dateKey];

    // Do not allow deleting last override
    if (preferences.length <= 1) {
      return;
    }

    setFieldValue(
      `[${cycleIndex}].groups[${periodKey}][${dateKey}]`,
      preferences.reduce<PreferenceInputsType[]>((list, current, index) => {
        if (index !== preferenceIndex) {
          return [...list, { ...current }];
        }

        if (current.operationType === OperationTypeEnums.ADD) {
          return [...list];
        }

        return [...list, { ...current, operationType: OperationTypeEnums.DELETE }];
      }, []),
    );
  };

  const handleAddPreference = (cycleIndex: number, periodKey: string, dateKey: string) => {
    const preferences = inputsList[cycleIndex].groups[periodKey][dateKey];

    const lastPreference = preferences[preferences.length - 1];

    setFieldValue(`[${cycleIndex}].groups[${periodKey}][${dateKey}]`, [
      ...preferences,
      {
        id: undefined,
        operationType: OperationTypeEnums.ADD,
        status: lastPreference.status,
        startTimeInSeconds: !isNil(lastPreference.startTimeInSeconds) ? 0 : undefined,
        endTimeInSeconds: !isNil(lastPreference.endTimeInSeconds) ? 0 : undefined,
      },
    ]);
  };

  const handleChangeStartTime = (
    value: string | undefined,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(
      `[${cycleIndex}].groups[${periodKey}][${dateKey}][${preferenceIndex}].startTimeInSeconds`,
      getSecondsFromTime(value),
    );
  };

  const handleChangeEndTime = (
    value: string | undefined,
    cycleIndex: number,
    periodKey: string,
    dateKey: string,
    preferenceIndex: number,
  ) => {
    setFieldValue(
      `[${cycleIndex}].groups[${periodKey}][${dateKey}][${preferenceIndex}].endTimeInSeconds`,
      getSecondsFromTime(value),
    );
  };

  const handleCancelPreferences = () => {
    setEditedCyclePeriodDate(undefined);
    resetForm();
    setApiError('');
  };

  const [openDeleteCycleModal, setOpenDeleteCycleModal] = useState(false);
  const [deletedCycle, setDeletedCycle] = useState<number>();

  const confirmDeleteCycle = async (cycleIndex: number) => {
    const inputs = inputsList[cycleIndex];

    if (!inputs) {
      return;
    }

    const payload: IUpsertAvailabilityCycleRequestDTO = {
      id: inputs.id,
      dayOfWeek: moment(inputs.cycleStart).day(),
      type: inputs.type,
      operationType: OperationTypeEnums.DELETE,
      cycleStart: inputs.cycleStart,
      validFrom: inputs.validFrom,
      cycleLength: inputs.cycleLength,
    };

    try {
      await updateCycle(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyDeletedYourCycle'), { type: toast.TYPE.SUCCESS });

      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    } finally {
      setDeletedCycle(undefined);
      setOpenDeleteCycleModal(false);
    }
  };

  const handleDeleteCycle = (cycleIndex: number) => {
    setDeletedCycle(cycleIndex);
    setOpenDeleteCycleModal(true);
  };

  const handleCloseDeleteCycleModal = () => {
    setOpenDeleteCycleModal(false);
  };

  const handleSubmitDeleteCycleModal = () => {
    if (isNil(deletedCycle)) {
      return;
    }

    confirmDeleteCycle(deletedCycle);
  };

  const [openDeleteDateModal, setOpenDeleteDateModal] = useState(false);
  const [deletedCyclePeriodDate, setDeletedCyclePeriodDate] = useState<EditedCyclePeriodDateType>();

  const confirmDeleteDate = async (cycleIndex: number, periodKey: string, dateKey: string) => {
    if (!inputsList) {
      return;
    }

    const inputs = inputsList[cycleIndex];
    const preferences = inputsList[cycleIndex]?.groups[periodKey]?.[dateKey];

    if (!inputs || !preferences.length) {
      return;
    }

    const [sharedPreference] = preferences;

    const dayOfCycle = sharedPreference.dayOfCycle;

    const payload: IUpsertAvailabilityPreferencesRequestDTO = {
      cycleId: inputs.id,
      dayOfCycle,
      preferences: preferences
        .filter((preference) => !isNil(preference.id))
        .map(({ id, status }) => ({
          operationType: OperationTypeEnums.DELETE,
          id,
          status,
        })),
    };

    try {
      await updatePreferences(payload).unwrap();

      toast.info(t('availability.youHaveSuccessfullyDeletedYourPreferences'), { type: toast.TYPE.SUCCESS });

      setApiError('');
    } catch (err) {
      const error = err as IError;
      console.error('Submit Error', { ...error });
      const errorMessage = getErrorMessage(error, t('global.oopsAnErrorOccurred'));
      toast.info(errorMessage, { type: toast.TYPE.ERROR });
      setApiError(errorMessage);
    } finally {
      setDeletedCyclePeriodDate(undefined);
      setOpenDeleteDateModal(false);
    }
  };

  const handleDeleteDate = (cycleIndex: number, periodKey: string, dateKey: string) => {
    setDeletedCyclePeriodDate({ cycleIndex, periodKey, dateKey });
    setOpenDeleteDateModal(true);
  };

  const handleCloseDeleteDateModal = () => {
    setOpenDeleteDateModal(false);
  };

  const handleSubmitDeleteDateModal = () => {
    if (!deletedCyclePeriodDate) {
      return;
    }

    const { cycleIndex, periodKey, dateKey } = deletedCyclePeriodDate;

    confirmDeleteDate(cycleIndex, periodKey, dateKey);
  };

  const deleteDateModalMessage = useMemo(() => {
    if (!deletedCyclePeriodDate || !inputsList) {
      return '';
    }

    const { cycleIndex, periodKey, dateKey } = deletedCyclePeriodDate;

    const inputs = inputsList[cycleIndex];
    const sharedPreference = inputsList[cycleIndex]?.groups[periodKey]?.[dateKey]?.[0];

    if (!inputs || !sharedPreference) {
      return '';
    }

    const periodIndex = Object.keys(inputs.groups).findIndex((key) => key === periodKey) + 1;

    return t(
      'availability.areYouSureYouWantToDeleteAllPreferencesFor',
      `${t('availability.futureCycle')} ${cycleIndex + 1},
      ${getAvailabilityDateText(inputs.type, periodIndex, sharedPreference.dayOfCycle, dateKey, t)}`,
    );
  }, [deletedCyclePeriodDate, inputsList]);

  const value: EmployeeAvailabilityFutureCyclesProps = {
    inputsList,
    touched,
    errors,
    loading: loading || isInitial,
    apiError,
    // Cycle
    updatingCycle: isUpdatingCycle,
    openCycle,
    editedCycle,
    onOpenCycleToggle: handleOpenCycleToggle,
    onEditedCycleChange: handleEditedCycleChange,
    onStartDateChange: handleStartDateChange,
    onCancelCycle: handleCancelCycle,
    onSaveCycle: handleUpsertCycle,
    // Preferences
    updatingPreferences: isUpdatingPreferences,
    editedCyclePeriodDate,
    onEditedCyclePeriodDateChange: handleEditedCyclePeriodDateChange,
    onChangeFullDay: handleChangeFullDay,
    onChangeStatus: handleChangeStatus,
    onDelete: handleDeleteOverride,
    onAdd: handleAddPreference,
    onChangeStartTime: handleChangeStartTime,
    onChangeEndTime: handleChangeEndTime,
    onCancelPreferences: handleCancelPreferences,
    onSavePreferences: handleSavePreferences,
    onDeleteCycle: handleDeleteCycle,
    onDeleteDate: handleDeleteDate,
  };

  return (
    <EmployeeAvailabilityFutureCyclesContext.Provider value={value}>
      {children}
      <Modal
        open={openDeleteCycleModal}
        onClose={handleCloseDeleteCycleModal}
        modalHeaderProps={{ title: t('availability.deleteCycle'), onClose: handleCloseDeleteCycleModal }}
        modalFooterProps={{
          submitText: t('global.delete'),
          onSubmit: handleSubmitDeleteCycleModal,
          closeText: t('global.cancel'),
          onClose: handleCloseDeleteCycleModal,
          loading: isUpdatingCycle,
          disabled: loading,
        }}
      >
        {!isNil(deletedCycle) && (
          <Typography>
            {t('availability.areYouSureYouWantToDeleteCycle', `${t('availability.futureCycle')} ${deletedCycle + 1}`)}
          </Typography>
        )}
      </Modal>
      <Modal
        open={openDeleteDateModal}
        onClose={handleCloseDeleteDateModal}
        modalHeaderProps={{ title: t('availability.deletePreferences'), onClose: handleCloseDeleteDateModal }}
        modalFooterProps={{
          submitText: t('global.delete'),
          onSubmit: handleSubmitDeleteDateModal,
          closeText: t('global.cancel'),
          onClose: handleCloseDeleteDateModal,
          loading: isUpdatingPreferences,
          disabled: loading,
        }}
      >
        <Typography>{deleteDateModalMessage}</Typography>
      </Modal>
    </EmployeeAvailabilityFutureCyclesContext.Provider>
  );
});
